import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import { TOONS, LINKS } from '@/data/content';

export default function ToonProfilePage() {
  const { id } = useParams();
  const idx = TOONS.findIndex((t) => t.id === id);
  const toon = TOONS[idx];

  useEffect(() => {
    if (toon) document.title = `${toon.role} — ARC TOONS`;
  }, [toon]);

  if (!toon) return <Navigate to="/toons" replace />;

  const prev = TOONS[(idx - 1 + TOONS.length) % TOONS.length];
  const next = TOONS[(idx + 1) % TOONS.length];

  return (
    <div
      data-testid="toon-profile"
      className="relative min-h-screen overflow-hidden pb-24 pt-32 sm:pt-36"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(55% 50% at 25% 40%, ${toon.accentSoft.replace('0.16', '0.26')} 0%, transparent 70%)`
        }}
      />
      <div className="relative mx-auto max-w-[1200px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Link
            to="/toons"
            data-testid="toon-profile-back"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-electric/80 transition-colors hover:text-electric"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> BACK TO ALL TOONS
          </Link>
        </motion.div>

        <div className="mt-10 grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center"
          >
            <div
              aria-hidden="true"
              className="absolute h-3/4 w-3/4 rounded-full blur-3xl"
              style={{ background: `radial-gradient(circle, ${toon.accent}44 0%, transparent 70%)` }}
            />
            <motion.img
              src={toon.imageFull}
              alt={`${toon.role} — ARC TOONS character artwork`}
              className="relative max-h-[52vh] w-auto object-contain"
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ filter: `drop-shadow(0 24px 50px rgba(5,11,24,0.7))` }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em]"
                style={{ borderColor: `${toon.accent}66`, color: toon.accent }}
              >
                TOON FILE // {toon.index}
              </span>
              <span className="rounded-full border border-dashed border-gold/60 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
                {toon.status}
              </span>
            </div>
            <h1
              className="mt-5 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl"
              style={{ color: toon.accent, textShadow: `0 0 34px ${toon.accent}55` }}
              data-testid="toon-profile-role"
            >
              {toon.role}
            </h1>
            <p className="mt-3 text-base font-semibold text-snow/85" data-testid="toon-profile-tagline">
              {toon.tagline}
            </p>

            <div className="mt-7 rounded-2xl border border-electric/15 bg-navy2/70 p-5" style={{ backdropFilter: 'blur(10px)' }}>
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-electric">PERSONALITY</p>
              <p className="mt-2 text-sm text-snow/80" data-testid="toon-profile-personality">
                {toon.personality}
              </p>
            </div>
            <div className="mt-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-electric">LORE</p>
              <p className="mt-2 text-sm leading-relaxed text-snow/70" data-testid="toon-profile-lore">
                {toon.lore}
              </p>
            </div>

            <a
              href={LINKS.buy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="toon-profile-buy-button"
              className="light-sweep mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-ember px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.2em] text-navy shadow-[0_0_32px_rgba(255,181,46,0.45)] transition-transform duration-200 hover:scale-[1.04]"
            >
              BUY $ARC TOONS <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </motion.div>
        </div>

        <div className="mt-16 flex items-center justify-between gap-4 border-t border-electric/15 pt-8" data-testid="toon-profile-nav">
          <Link
            to={`/toons/${prev.id}`}
            data-testid="toon-profile-prev"
            className="group flex items-center gap-3 text-left"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-electric/30 text-electric transition-all group-hover:shadow-[0_0_18px_rgba(0,200,255,0.5)]">
              <ArrowLeft className="h-4 w-4" />
            </span>
            <span>
              <span className="block font-mono text-[8px] uppercase tracking-[0.3em] text-snow/40">PREV TOON</span>
              <span className="block font-display text-xs font-bold uppercase tracking-[0.15em] text-snow/80">
                {prev.role}
              </span>
            </span>
          </Link>
          <Link
            to={`/toons/${next.id}`}
            data-testid="toon-profile-next"
            className="group flex items-center gap-3 text-right"
          >
            <span>
              <span className="block font-mono text-[8px] uppercase tracking-[0.3em] text-snow/40">NEXT TOON</span>
              <span className="block font-display text-xs font-bold uppercase tracking-[0.15em] text-snow/80">
                {next.role}
              </span>
            </span>
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-electric/30 text-electric transition-all group-hover:shadow-[0_0_18px_rgba(0,200,255,0.5)]">
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}
